import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LocalizedText } from '@cr/shared';
import { api } from '@/lib/api';
import { localized } from '@/lib/format';
import { useSession } from '@/lib/session';
import Icon, { isIconName } from '@/components/icon';
import { ProductGrid, type ProductSummary } from '@/components/product-card';
import { CardSkeleton, EmptyState, SectionTitle, Skeleton } from '@/components/ui';
import type { CategoryNode } from './categories';

interface Banner {
  id: string;
  title: LocalizedText | null;
  subtitle: LocalizedText | null;
  imageUrl: string;
  linkType: 'PRODUCT' | 'CATEGORY' | 'URL' | 'NONE';
  linkValue: string | null;
}

interface ProductList {
  items: ProductSummary[];
  total: number;
}

export default function HomePage() {
  const { t } = useTranslation();
  const { authorized } = useSession();

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<CategoryNode[]>('/catalog/categories')).data,
  });

  const banners = useQuery({
    queryKey: ['banners'],
    queryFn: async () => (await api.get<Banner[]>('/catalog/banners')).data,
  });

  const popular = useQuery({
    queryKey: ['products', 'popular'],
    queryFn: async () =>
      (await api.get<ProductList>('/catalog/products', { params: { sort: 'popular', limit: 6 } }))
        .data,
  });

  const fresh = useQuery({
    queryKey: ['products', 'new'],
    queryFn: async () =>
      (await api.get<ProductList>('/catalog/products', { params: { sort: 'new', limit: 6 } })).data,
  });

  const favorites = useQuery({
    queryKey: ['favorites'],
    queryFn: async () => (await api.get<Array<{ id: string }>>('/favorites')).data,
    enabled: authorized,
  });

  const favoriteIds = new Set((favorites.data ?? []).map((f) => f.id));
  const nothingAtAll = popular.data?.total === 0 && fresh.data?.total === 0;

  return (
    <div className="space-y-7 px-4 pb-8 pt-1 sm:px-6">
      <BannerCarousel banners={banners.data ?? []} />

      <QuickCategories categories={categories.data ?? []} loading={categories.isLoading} />

      {nothingAtAll && <EmptyState title={t('home.empty')} />}

      <ProductSection
        title={t('home.popular')}
        data={popular.data}
        loading={popular.isLoading}
        favoriteIds={favoriteIds}
        moreHref="/catalog?sort=popular"
      />

      <ProductSection
        title={t('home.newArrivals')}
        data={fresh.data}
        loading={fresh.isLoading}
        favoriteIds={favoriteIds}
        moreHref="/catalog?sort=new"
      />
    </div>
  );
}

/** Баннеры листаются свайпом; активную точку считаем по позиции прокрутки. */
function BannerCarousel({ banners }: { banners: Banner[] }) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  if (banners.length === 0) return null;

  function onScroll() {
    const el = trackRef.current;
    if (el) setActive(Math.round(el.scrollLeft / el.clientWidth));
  }

  return (
    <section>
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto"
      >
        {banners.map((banner) => (
          <BannerCard key={banner.id} banner={banner} label={t('home.bannerAction')} />
        ))}
      </div>

      {banners.length > 1 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {banners.map((banner, i) => (
            <span
              key={banner.id}
              className={`h-1.5 rounded-pill transition-all ${
                i === active ? 'w-5 bg-bordo' : 'w-1.5 bg-line'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function BannerCard({ banner, label }: { banner: Banner; label: string }) {
  const target =
    banner.linkType === 'PRODUCT'
      ? `/product/${banner.linkValue}`
      : banner.linkType === 'CATEGORY'
        ? `/catalog?categoryId=${banner.linkValue}`
        : null;

  const content = (
    <div
      className="relative w-full flex-none snap-center overflow-hidden rounded-card bg-bordo p-6 text-left text-white"
      style={
        banner.imageUrl
          ? {
              backgroundImage: `linear-gradient(rgb(110 36 51 / 0.85), rgb(85 27 40 / 0.9)), url(${banner.imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
    >
      {banner.title && (
        <h3 className="mt-4 font-display text-[30px] font-bold leading-tight">
          {localized(banner.title)}
        </h3>
      )}
      {banner.subtitle && (
        <p className="mt-1 max-w-sm text-sm text-white/85">{localized(banner.subtitle)}</p>
      )}
      {target && (
        <span className="mt-5 inline-flex items-center gap-1 rounded-xl bg-surface px-5 py-2.5 text-sm font-semibold text-bordo">
          {label} <Icon name="chevron" size={16} />
        </span>
      )}
    </div>
  );

  return target ? (
    <Link to={target} className="w-full flex-none snap-center active:opacity-90">
      {content}
    </Link>
  ) : (
    content
  );
}

/** Верхний уровень дерева — быстрый вход в каталог прямо с главной. */
function QuickCategories({
  categories,
  loading,
}: {
  categories: CategoryNode[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="-mx-4 flex gap-2.5 overflow-hidden px-4 sm:-mx-6 sm:px-6">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[76px] w-[76px] shrink-0 rounded-2xl" />
        ))}
      </div>
    );
  }

  if (categories.length === 0) return null;

  return (
    <div className="no-scrollbar -mx-4 flex gap-2.5 overflow-x-auto px-4 sm:-mx-6 sm:px-6">
      {categories.map((category) => (
        <Link
          key={category.id}
          to={`/catalog?categoryId=${category.id}`}
          className="flex w-[76px] flex-none flex-col items-center gap-1.5 rounded-2xl bg-surface px-2 py-3 text-bordo shadow-soft active:scale-95"
        >
          {category.imageUrl ? (
            <img
              src={category.imageUrl}
              alt=""
              loading="lazy"
              className="size-[26px] rounded-full object-cover"
            />
          ) : (
            <Icon name={isIconName(category.icon) ? category.icon : 'shirt'} size={26} />
          )}
          <span className="line-clamp-2 text-center text-[12px] font-medium leading-tight text-ink">
            {localized(category.name)}
          </span>
        </Link>
      ))}
    </div>
  );
}

function ProductSection({
  title,
  data,
  loading,
  favoriteIds,
  moreHref,
}: {
  title: string;
  data?: ProductList;
  loading: boolean;
  favoriteIds: Set<string>;
  moreHref: string;
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <section>
        <SectionTitle>{title}</SectionTitle>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {[0, 1].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </section>
    );
  }

  if (!data || data.items.length === 0) return null;

  return (
    <section>
      <SectionTitle
        action={
          <Link
            to={moreHref}
            className="flex shrink-0 items-center gap-1 text-sm font-medium text-bordo"
          >
            {t('home.showAll')}
            <Icon name="chevron" size={16} />
          </Link>
        }
      >
        {title}
      </SectionTitle>
      <ProductGrid products={data.items} favorites={favoriteIds} />
    </section>
  );
}
